// logoAndText.js
import * as THREE from 'three';
import { TTFLoader } from 'three/examples/jsm/loaders/TTFLoader.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';

/**
 * Sets up the logo planes and text on the camera.
 * Creates: 
 * - "JASON GODFREY" 
 * - "~ SOFTWARE ENGINEERING ~"
 * - "about example text" (hidden by default, in 3 lines)
 * - "ABOUT"
 * - "CONTACT"
 * - "Home" (hidden by default)
 *
 * Additional refs for the outline and logo meshes are stored.
 */
export function setupLogoAndText({
  camera,
  scene,
  glowMaterialRef,
  textMaterialRef,
  subTextMeshRef,
  aboutMeshRef,
  aboutHitboxRef,
  contactMeshRef,
  contactHitboxRef,
  homeMeshRef,
  homeHitboxRef,
  outlineMeshRef,
  logoMeshRef,
  aboutExampleMeshRef, // <--- ref for multi-line about text
  aboutContactMeshRef,
  aboutContactHitboxRef,
}) {
  // --- LOGO PLANES ---
  const textureLoader = new THREE.TextureLoader();
  const logoTexture = textureLoader.load('/logo3.png');
  const glowColor = new THREE.Color(0x00aaff); //make this blue: 0x00aaff

  const glowMaterial = new THREE.MeshStandardMaterial({
    map: logoTexture,
    transparent: true,
    color: glowColor,
    emissive: glowColor,
    emissiveIntensity: 1.0,
    opacity: 0.12,
  });
  glowMaterialRef.current = glowMaterial;

  // (Optional glowPlane, not added to scene)
  const glowPlane = new THREE.Mesh(new THREE.PlaneGeometry(10, 10), glowMaterial);
  glowPlane.position.set(0, 0, -30.5);

  const outlineMaterial = new THREE.MeshPhysicalMaterial({
    map: logoTexture, 
    color: 0x000000,
  
    // Enable physically based transparency
    transmission: 0,  // or 1.0 for fully transmissive
    thickness: 10.0,     // "thickness" in world units for refraction
  
    // Additional properties for glass-like effect
    roughness: 0.0,
    metalness: 0.0,
    ior: 1.45,  // index of refraction, typical for glass
  
    // Keep or remove transparency/opacity
    transparent: true,
    opacity: 1.0,
  });
  const outlinePlane = new THREE.Mesh(new THREE.PlaneGeometry(10, 10), outlineMaterial);
  outlinePlane.position.set(0, 10.5, -30.2);
  //camera.add(outlinePlane);
  if (outlineMeshRef) outlineMeshRef.current = outlinePlane;

  // Logo plane
  const logoMaterial = new THREE.MeshBasicMaterial({
    map: logoTexture,
    transparent: true,
    opacity: 0.5,
  });
  const logoPlane = new THREE.Mesh(new THREE.PlaneGeometry(10, 10), logoMaterial);
  logoPlane.position.set(0, 10.5, -30);
  //camera.add(logoPlane);
  if (logoMeshRef) logoMeshRef.current = logoPlane;

  // --- TEXT LOADING ---
  const ttfLoader = new TTFLoader();
  ttfLoader.load('/Coelacanth.ttf', (ttfData) => {
    const font = new FontLoader().parse(ttfData);

    // MAIN TITLE: "JASON GODFREY"
    const textGeo = new TextGeometry('JASON GODFREY', {
      font,
      size: 15.5,
      height: 0,
      curveSegments: 8,
      bevelEnabled: true,
      bevelThickness: 0.2,
      bevelSize: 0.4,
      bevelSegments: 2,
    });
    const textMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.385,
    });
    textMaterialRef.current = textMat;
    const textMesh = new THREE.Mesh(textGeo, textMat);
    textMesh.position.set(-7, -4.5, -30);
    textMesh.scale.set(0.075, 0.075, 0.004);
    camera.add(textMesh);

    // SUBTEXT: "~ SOFTWARE ENGINEERING ~"
    const subTextGeo = new TextGeometry('~ SOFTWARE ENGINEERING ~', {
      font,
      size: 7,
      height: 0,
      curveSegments: 8,
      bevelEnabled: false,
    });
    const subTextMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.385,
    });
    const subTextMesh = new THREE.Mesh(subTextGeo, subTextMat);
    subTextMesh.position.set(-4.7, -5.9, -30);
    subTextMesh.scale.set(0.065, 0.065, 0.004);
    camera.add(subTextMesh);
    subTextMeshRef.current = subTextMesh;

    // ABOUT EXAMPLE TEXT (3 lines, styled like subtext)
    const aboutText3Lines = ``; //\nFULL-STACK SOFTWARE ENGINEERING \n//specialized in crafting immersive web and game experiences.`

    const aboutExampleGeo = new TextGeometry(aboutText3Lines, {
      font,
      size: 7,
      height: 0,
      curveSegments: 8,
      bevelEnabled: false,
    });
    const aboutExampleMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.0, // start fully hidden
    });
    const aboutExampleMesh = new THREE.Mesh(aboutExampleGeo, aboutExampleMat);

    // Place it below the subtext
    aboutExampleMesh.position.set(-4.7, -3.3, -30);
    aboutExampleMesh.scale.set(0.085, 0.085, 0.004);

    // Start invisible
    aboutExampleMesh.visible = false;
    camera.add(aboutExampleMesh);

    // Store ref if needed
    if (aboutExampleMeshRef) {
      aboutExampleMeshRef.current = aboutExampleMesh;
    }

    // ABOUT TEXT
    const aboutGeo = new TextGeometry('CONTACT', {
      font,
      size: 12,
      height: 0,
      curveSegments: 8,
      bevelEnabled: false,
    });
    const aboutMat = subTextMat.clone();
    const aboutMesh = new THREE.Mesh(aboutGeo, aboutMat);
    aboutMesh.position.set(-3, -9.2, -30);
    aboutMesh.scale.set(0.065, 0.065, 0.004);
    //camera.add(aboutMesh);
    aboutMeshRef.current = aboutMesh;

    // Create hitbox for ABOUT
    aboutGeo.computeBoundingBox();
    const aboutBox = aboutGeo.boundingBox;
    const aboutWidth = aboutBox.max.x - aboutBox.min.x;
    const aboutHeight = aboutBox.max.y - aboutBox.min.y;
    const aboutPlaneGeom = new THREE.PlaneGeometry(aboutWidth, aboutHeight);
    const aboutHitboxMat = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
    const aboutHitbox = new THREE.Mesh(aboutPlaneGeom, aboutHitboxMat);
    aboutHitbox.scale.copy(aboutMesh.scale);
    const aboutCenterX = (aboutBox.min.x + aboutBox.max.x) * 0.5 * aboutMesh.scale.x;
    const aboutCenterY = (aboutBox.min.y + aboutBox.max.y) * 0.5 * aboutMesh.scale.y;
    aboutHitbox.position.set(
      aboutMesh.position.x + aboutCenterX,
      aboutMesh.position.y + aboutCenterY,
      aboutMesh.position.z
    );
    aboutHitbox.rotation.copy(aboutMesh.rotation);
    camera.add(aboutHitbox);
    aboutHitboxRef.current = aboutHitbox;

    // CONTACT TEXT
    const contactGeo = new TextGeometry('CONTACT', {
      font,
      size: 12,
      height: 0,
      curveSegments: 8,
      bevelEnabled: false,
    });
    const contactMat = subTextMat.clone();
    const contactMesh = new THREE.Mesh(contactGeo, contactMat);
    contactMesh.position.set(0, -9.2, -30);
    contactMesh.scale.set(0.065, 0.065, 0.004);
    //camera.add(contactMesh);
    contactMeshRef.current = contactMesh;

    // Create hitbox for CONTACT
    contactGeo.computeBoundingBox();
    const contactBox = contactGeo.boundingBox;
    const contactWidth = contactBox.max.x - contactBox.min.x;
    const contactHeight = contactBox.max.y - contactBox.min.y;
    const contactPlaneGeom = new THREE.PlaneGeometry(contactWidth, contactHeight);
    const contactHitboxMat = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
    const contactHitbox = new THREE.Mesh(contactPlaneGeom, contactHitboxMat);
    contactHitbox.scale.copy(contactMesh.scale);
    const contactCenterX = (contactBox.min.x + contactBox.max.x) * 0.5 * contactMesh.scale.x;
    const contactCenterY = (contactBox.min.y + contactBox.max.y) * 0.5 * contactMesh.scale.y;
    contactHitbox.position.set(
      contactMesh.position.x + contactCenterX,
      contactMesh.position.y + contactCenterY,
      contactMesh.position.z
    );
    contactHitbox.rotation.copy(contactMesh.rotation);
    //camera.add(contactHitbox);
    contactHitboxRef.current = contactHitbox;

    // HOME TEXT (hidden by default)
    const homeGeo = new TextGeometry('', {
      font,
      size: 12,
      height: 0,
      curveSegments: 8,
      bevelEnabled: false,
    });
    const homeMat = subTextMat.clone();
    const homeMesh = new THREE.Mesh(homeGeo, homeMat);
    homeMesh.position.set(-5, -9.2, -30);
    homeMesh.scale.set(0.065, 0.065, 0.004);
    homeMesh.visible = false;
    camera.add(homeMesh);
    homeMeshRef.current = homeMesh;

    // Create hitbox for Home
    homeGeo.computeBoundingBox();
    const homeBox = homeGeo.boundingBox;
    const homeWidth = homeBox.max.x - homeBox.min.x;
    const homeHeight = homeBox.max.y - homeBox.min.y;
    const homePlaneGeom = new THREE.PlaneGeometry(homeWidth, homeHeight);
    const homeHitboxMat = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
    const homeHitbox = new THREE.Mesh(homePlaneGeom, homeHitboxMat);
    homeHitbox.scale.copy(homeMesh.scale);
    const homeCenterX = (homeBox.min.x + homeBox.max.x) * 0.5 * homeMesh.scale.x;
    const homeCenterY = (homeBox.min.y + homeBox.max.y) * 0.5 * homeMesh.scale.y;
    homeHitbox.position.set(
      homeMesh.position.x + homeCenterX,
      homeMesh.position.y + homeCenterY,
      homeMesh.position.z
    );
    homeHitbox.rotation.copy(homeMesh.rotation);
    camera.add(homeHitbox);
    homeHitbox.visible = false;
    homeHitboxRef.current = homeHitbox;


    // about CONTACT TEXT (hidden by default)
    const aboutContactGeo = new TextGeometry('CONTACT', {
      font,
      size: 12,
      height: 0,
      curveSegments: 8,
      bevelEnabled: false,
    });
    const aboutContactMat = subTextMat.clone();
    const aboutContactMesh = new THREE.Mesh(aboutContactGeo, aboutContactMat);
    aboutContactMesh.position.set(5, -9.2, -30);
    aboutContactMesh.scale.set(0.065, 0.065, 0.004);
    aboutContactMesh.visible = false;
    camera.add(aboutContactMesh);
    aboutContactMeshRef.current = aboutContactMesh;

    // Create hitbox for Home
    aboutContactGeo.computeBoundingBox();
    const aboutContactBox = aboutContactGeo.boundingBox;
    const aboutContactWidth = aboutContactBox.max.x - aboutContactBox.min.x;
    const aboutContactHeight = aboutContactBox.max.y - aboutContactBox.min.y;
    const aboutContactPlaneGeom = new THREE.PlaneGeometry(aboutContactWidth, aboutContactHeight);
    const aboutContactHitboxMat = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
    const aboutContactHitbox = new THREE.Mesh(aboutContactPlaneGeom, aboutContactHitboxMat);
    aboutContactHitbox.scale.copy(aboutContactMesh.scale);
    const aboutContactCenterX = (aboutContactBox.min.x + aboutContactBox.max.x) * 0.5 * aboutContactMesh.scale.x;
    const aboutContactCenterY = (aboutContactBox.min.y + aboutContactBox.max.y) * 0.5 * aboutContactMesh.scale.y;
    aboutContactHitbox.position.set(
      aboutContactMesh.position.x + aboutContactCenterX,
      aboutContactMesh.position.y + aboutContactCenterY,
      aboutContactMesh.position.z
    );
    aboutContactHitbox.rotation.copy(aboutContactMesh.rotation);
    camera.add(aboutContactHitbox);
    aboutContactHitbox.visible = false;
    aboutContactHitboxRef.current = aboutContactHitbox;
  });
}
